import type {Pagination} from "@hosttools/core/shared/model/pagination";
import {
    Box,
    Button,
    ButtonIcon,
    ButtonSpinner,
    ButtonText,
    EmptyState,
    HStack,
    memoWithType,
    PointerDownIcon,
    Skeleton,
    Table,
    TableCell,
    TableHead,
    TableHeader,
    TablePagination,
    TableRow,
    useMobileView,
    useToast,
    VStack
} from "@hosttools/frontend";
import type {QueryObserverBaseResult} from "@tanstack/react-query";
import {useQuery} from "@tanstack/react-query";
import type {
    Column,
    ColumnDef,
    ColumnFiltersState,
    FiltersTableState,
    PaginationState,
    Row,
    RowSelectionState,
    SortingState,
    Table as TableRT,
    TableState,
    VisibilityState
} from "@tanstack/react-table";
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from "@tanstack/react-table";
import classNames from "classnames";
import React, {
    forwardRef,
    type ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";

import {defaultPageSize} from "./constant";
import EditColumns from "./EditColumns";
import FilterDateRange from "./FilterDateRange";
import FilterSelect from "./FilterSelect";
import FilterText from "./FilterText";

import {type RequestPayload} from "@/admin/hooks/usePaginatedFetch";

export type FetchDataOptions = RequestPayload &
    FiltersTableState & {
        sorting: SortingState;
    };

interface BaseProps<T> {
    testID?: string;
    enableGlobalFilter?: boolean;
    enableVisibility?: boolean;
    enableRowSelection?: boolean | ((row: Row<T>) => boolean);
    enableMultiRowSelection?: boolean;
    pageNumber?: number;
    pageSize?: number;
    hiddenColumns?: (keyof T)[];
    gridTemplateColumnsClass?: string;
    variant?: "table" | "list";
    emptyState?: ReactNode;
    exportData?: (payload: FetchDataOptions) => Promise<void>;
    onRowClick?: (row: Row<T>) => void;
    onStateChange?: (state: TableState) => void;
}

interface PropsOffline<T> extends BaseProps<T> {
    mode: "offline";
    isLoading?: boolean;
    data: T[];
    columns: ColumnDef<T, string>[];
    rowId?: keyof T;
}

interface PropsOnline<T> extends BaseProps<T> {
    mode: "online";
    queryKey: string;
    columns: ColumnDef<T, any>[];
    queryFn: (payload: FetchDataOptions) => Promise<Pagination<T> | undefined>;
    rowId?: keyof T;
}

export interface RefValue<T> {
    table: TableRT<T>;
    refetch: QueryObserverBaseResult<Pagination<T> | undefined>["refetch"];
}

type Props<T> = StrictUnion<PropsOffline<T> | PropsOnline<T>>;

const TableFetch = forwardRef(
    (
        {
            testID,
            columns,
            rowId,
            enableGlobalFilter = true,
            enableRowSelection = true,
            enableMultiRowSelection = true,
            mode,
            isLoading: isLoadingOffline,
            data: offlineData,
            queryKey,
            pageNumber = 1,
            pageSize = defaultPageSize,
            enableVisibility,
            hiddenColumns,
            gridTemplateColumnsClass,
            variant = "table",
            emptyState,
            exportData,
            queryFn,
            onStateChange,
            onRowClick,
            isLoading: offlineIsLoading
        },
        ref
    ) => {
        const isMobile = useMobileView();
        const toast = useToast();
        const [globalFilter, setGlobalFilter] = useState<FiltersTableState>();
        const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
            hiddenColumns
                ? hiddenColumns.reduce((arr, elem) => {
                      arr[elem] = false;
                      return arr;
                  }, {} as VisibilityState)
                : {}
        );
        const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
        const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
        const [pagination, setPagination] = useState<PaginationState>({
            pageIndex: pageNumber - 1,
            pageSize
        });
        const [sorting, setSorting] = React.useState<SortingState>([]);
        const [totalPages, setTotalPages] = useState<number | undefined>();
        const [exportLoading, setExportLoading] = useState<boolean>();
        const fetchDataOptions: FetchDataOptions = useMemo(
            () => ({
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sorting,
                columnFilters,
                globalFilter
            }),
            [pagination, columnFilters, globalFilter, sorting]
        );

        const {data, isLoading, refetch} = useQuery({
            queryKey: [...(mode === "online" ? [queryKey] : []), fetchDataOptions],
            enabled: mode === "online",
            queryFn: async () => {
                if (mode === "online") {
                    return queryFn(fetchDataOptions);
                }
            }
        });

        const docs = data?.docs;

        const tableData = useMemo(() => {
            if (mode === "offline") {
                return isLoadingOffline
                    ? (Array(pageSize).fill({}) as NonNullable<typeof offlineData>)
                    : offlineData;
            }
            return isLoading ? (Array(pageSize).fill({}) as NonNullable<typeof docs>) : docs ?? [];
        }, [mode, isLoading, isLoadingOffline, pageSize, docs, offlineData]);

        const tableColumns = useMemo(() => {
            return isLoading || isLoadingOffline
                ? (columns.map(column => ({
                      ...column,
                      header: column.header,
                      cell: () => (<Skeleton className="h-5 w-20" />) as ReactNode
                  })) as typeof columns)
                : columns;
        }, [isLoading, columns, isLoadingOffline]);

        const state = useMemo(
            () => ({
                pagination,
                rowSelection,
                columnFilters,
                globalFilter,
                columnVisibility,
                sorting
            }),
            [pagination, rowSelection, columnFilters, globalFilter, columnVisibility, sorting]
        );

        const table = useReactTable({
            getRowId: rowId ? row => row[rowId] : undefined,
            columns: tableColumns,
            data: tableData,
            manualFiltering: mode === "online",
            manualSorting: mode === "online",
            manualPagination: mode === "online",
            pageCount: mode === "online" ? totalPages ?? -1 : undefined,
            state,
            enableGlobalFilter,
            // not sure if this should only get set for offline mode
            autoResetPageIndex: false,
            enableColumnFilters: true,
            enableMultiRowSelection,
            enableRowSelection,
            onSortingChange: setSorting,
            onRowSelectionChange: setRowSelection,
            onPaginationChange: setPagination,
            onColumnFiltersChange: setColumnFilters,
            onGlobalFilterChange: setGlobalFilter,
            getCoreRowModel: getCoreRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            // automatically sorting
            getSortedRowModel: getSortedRowModel(),
            onColumnVisibilityChange: setColumnVisibility,
            debugTable: __DEV__
        });

        // this makes code more safer
        const onStateChangeRef = useRef(onStateChange);
        onStateChangeRef.current = onStateChange;

        // this effect looks unsafe
        useEffect(() => {
            const value = table.getState();
            onStateChangeRef.current?.(value);
        }, [state, table]);

        const filterColumns = useMemo(() => {
            const result: Column<{}, unknown>[] = [];
            table.getHeaderGroups().forEach(group => {
                group.headers.forEach(({column}) => {
                    if (column.getCanFilter() && column.columnDef.filter) {
                        result.push(column);
                    }
                });
            });
            return result;
        }, [table]);

        const handleExportData = useCallback(async () => {
            setExportLoading(true);
            try {
                await exportData?.(fetchDataOptions);
                toast.success("Exported reservations");
            } finally {
                setExportLoading(false);
            }
        }, [exportData, fetchDataOptions, toast]);

        const isEmpty =
            mode === "online"
                ? !isLoading && !tableData.length
                : !offlineIsLoading && table.getPageCount() === 0;

        useImperativeHandle(
            ref,
            () => ({
                table,
                refetch
            }),
            [refetch, table]
        );

        // keep track the loaded `totalPages` because it turns empty as start loading with new key
        useEffect(() => {
            if (typeof data?.totalPages === "number") {
                setTotalPages(data.totalPages);
            }
        }, [data?.totalPages]);

        // handle online mode
        // go to previous page in case of no more data of current page
        const hasNoData = !tableData.length && (mode === "online" ? !isLoading : !offlineIsLoading);
        useEffect(() => {
            if (mode === "online" && hasNoData) {
                setPagination(prev => {
                    if (prev.pageIndex > 0) {
                        return {...prev, pageIndex: prev.pageIndex - 1};
                    }
                    return prev;
                });
            }
        }, [mode, hasNoData]);

        return (
            <VStack className="lg:gap-4 min-h-[auto]" space="sm" testID="table-wrapper">
                {(filterColumns.length || enableGlobalFilter) && (
                    <Box
                        className={`
                            flex
                            flex-col-reverse
                            md:flex-row
                            px-5
                            md:px-0
                            justify-between
                            z-auto
                            flex-wrap
                            gap-3
                            min-h-[auto]
                        `}
                    >
                        {filterColumns.length > 0 && (
                            <Box
                                className={`
                                    flex
                                    max-w-full
                                    flex-col
                                    md:flex-row
                                    flex-wrap
                                    gap-3
                                `}
                            >
                                {filterColumns.map(column => {
                                    const filter = column.columnDef?.filter;
                                    if (filter?.type === "select") {
                                        return (
                                            <FilterSelect
                                                key={column.id}
                                                className={filter.className}
                                                column={column}
                                                isMulti={filter.isMulti}
                                                isOptionDisabled={filter.isOptionDisabled}
                                                name={filter.name}
                                                table={table}
                                            />
                                        );
                                    }
                                    if (filter?.type === "text") {
                                        return (
                                            <FilterText
                                                key={column.id}
                                                className={filter.className}
                                                column={column}
                                                kind="column"
                                                placeholder={filter.placeholder}
                                                table={table}
                                            />
                                        );
                                    }
                                    if (filter?.type === "dateRange") {
                                        return (
                                            <FilterDateRange
                                                key={column.id}
                                                className={filter.className}
                                                column={column}
                                                defaultEndDate={filter.defaultEndDate}
                                                defaultStartDate={filter.defaultStartDate}
                                                table={table}
                                            />
                                        );
                                    }
                                    return null;
                                })}
                            </Box>
                        )}
                        <HStack className="flex-col md:flex-row" space="md">
                            {enableGlobalFilter && <FilterText kind="table" table={table} />}
                            {exportData && (
                                <Button
                                    action="secondary"
                                    size="sm"
                                    variant="outline"
                                    onPress={handleExportData}
                                >
                                    {exportLoading && <ButtonSpinner />}
                                    <ButtonIcon as={PointerDownIcon} />
                                    <ButtonText>
                                        {exportLoading ? "Exporting" : "Export"}
                                    </ButtonText>
                                </Button>
                            )}
                            {enableVisibility && (
                                <EditColumns
                                    columnVisibility={columnVisibility}
                                    columns={table.getAllColumns()}
                                />
                            )}
                        </HStack>
                    </Box>
                )}

                {isEmpty && columnFilters.length < 1 && !globalFilter ? (
                    emptyState ?? <EmptyState primaryText="No data available" />
                ) : (
                    <Table
                        isWeb
                        gridTemplateColumnsClass={gridTemplateColumnsClass}
                        testID={testID}
                    >
                        {variant === "table" && (
                            <TableHeader>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            if (
                                                isMobile &&
                                                header.column.columnDef.responsive === "md"
                                            ) {
                                                return null;
                                            }
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    {...(header.column.getCanSort() && {
                                                        onPress:
                                                            header.column.getToggleSortingHandler()
                                                    })}
                                                    align={header.column.columnDef.align}
                                                    sortDir={header.column.getIsSorted()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                        )}

                        {table.getRowModel().rows.map(row => (
                            <TableRow key={row.id} row={row} onRowClick={onRowClick}>
                                {row.getVisibleCells().map(cell => {
                                    if (isMobile && cell.column.columnDef.responsive === "md") {
                                        return null;
                                    }
                                    return (
                                        <TableCell
                                            key={cell.id}
                                            align={cell.column.columnDef.align}
                                            className={classNames(
                                                cell.column.columnDef.className,
                                                cell.column.columnDef.styleCell?.(row.original)
                                            )}
                                            visibleOnHover={cell.column.columnDef.visibleOnHover}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </Table>
                )}

                {((mode === "online" && totalPages) || mode === "offline") &&
                    !isEmpty &&
                    table.getPageCount() > 1 && (
                        <HStack className="justify-center md:justify-end">
                            <TablePagination
                                currentPage={table.getState().pagination.pageIndex + 1}
                                hasNextPage={table.getCanNextPage()}
                                hasPrevPage={table.getCanPreviousPage()}
                                testID="table-pagination"
                                totalPages={table.getPageCount()}
                                onChangePage={table.setPageIndex}
                                onNext={table.nextPage}
                                onPrevious={table.previousPage}
                            />
                        </HStack>
                    )}
            </VStack>
        );
    }
) as {
    // this is sort of hack to allow pass our generic for `forwardRef`
    <T extends {}>(p: Props<T> & {ref?: Ref<RefValue<T>>}): React.ReactElement;
    displayName: string;
};

TableFetch.displayName = "forwardRef(TableFetch)";

export default memoWithType(TableFetch);
